import { EventNames, globalEventBus } from "@littlebird/event-bus";
import { Logger } from "@littlebird/logger";
import fetchRetry from "fetch-retry";
import { jwtDecode } from "jwt-decode";
//#region src/adapters/local-storage-adapter.ts
/** LocalStorage adapter for token storage with JSON parsing and error handling. */
const logger = Logger.create({ context: "LocalStorageAdapter" });
const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
var LocalStorageAdapter = class {
	getAccessToken() {
		return this.getItem(ACCESS_TOKEN_KEY);
	}
	setAccessToken(token) {
		this.setItem(ACCESS_TOKEN_KEY, token);
	}
	getRefreshToken() {
		return this.getItem(REFRESH_TOKEN_KEY);
	}
	setRefreshToken(token) {
		this.setItem(REFRESH_TOKEN_KEY, token);
	}
	clearTokens() {
		localStorage.removeItem(ACCESS_TOKEN_KEY);
		localStorage.removeItem(REFRESH_TOKEN_KEY);
	}
	getItem(key) {
		try {
			const item = localStorage.getItem(key);
			if (item === null || item === void 0 || item === "undefined") return null;
			return JSON.parse(item);
		} catch (error) {
			logger.error(`Failed to parse localStorage item "${key}"`, { error });
			return null;
		}
	}
	setItem(key, value) {
		try {
			localStorage.setItem(key, JSON.stringify(value));
		} catch (error) {
			logger.error(`Failed to set localStorage item "${key}"`, { error });
		}
	}
};
//#endregion
//#region src/errors/network-errors.ts
/** Network error class for connectivity issues. */
var NetworkError = class NetworkError extends Error {
	constructor(message = "Network error") {
		super(message);
		this.name = "NetworkError";
		Object.setPrototypeOf(this, NetworkError.prototype);
	}
};
/** Check if an error is a network error (excludes deliberate AbortError cancellations). */
function isNetworkError(error) {
	if (error instanceof NetworkError) return true;
	if (!(error instanceof Error)) return false;
	if (error.name === "AbortError") return false;
	const message = error.message.toLowerCase();
	return message.includes("failed to fetch") || message.includes("network request failed") || message.includes("networkerror") || message.includes("network error") || message.includes("load failed") || message.includes("fetch failed") || message.includes("net::err_") || message.includes("econnrefused") || message.includes("enotfound") || message.includes("etimedout") || message.includes("econnreset");
}
//#endregion
//#region src/adapters/electron-platform-adapter.ts
/** Electron platform adapter for auth operations via IPC. */
/** Electron-specific platform adapter using IPC. */
var ElectronPlatformAdapter = class {
	async requestToken(source, options) {
		try {
			const response = await window.authApi?.requestToken(source, options);
			if (!response) return {
				status: "failed",
				reason: "network_failure",
				error: "authApi not available"
			};
			if (response.status === "failed") return {
				status: "failed",
				reason: response.reason,
				error: response.error
			};
			return {
				status: "success",
				accessToken: response.accessToken,
				refreshToken: response.refreshToken
			};
		} catch (error) {
			return {
				status: "failed",
				reason: isNetworkError(error) ? "network_failure" : "unknown",
				error: error instanceof Error ? error.message : "Unknown error"
			};
		}
	}
	async clearAuthData(reason) {
		await window.authApi?.clearAuthData(reason);
	}
};
//#endregion
//#region src/core/platform-detection.ts
/** Platform detection utilities. */
/** Check if running in Electron environment with our preload bridge available. */
function isElectronPlatform() {
	return typeof navigator === "object" && typeof navigator.userAgent === "string" && navigator.userAgent.toLowerCase().includes(" electron/") && typeof window !== "undefined" && typeof window.authApi !== "undefined";
}
/** Create platform-specific auth adapter. Returns undefined for web browsers. */
function createPlatformAdapter() {
	if (!isElectronPlatform()) return;
	return new ElectronPlatformAdapter();
}
//#endregion
//#region src/events/index.ts
/** Auth package event definitions. */
/** Auth event names (re-exported from event-bus). */
const AuthEventNames = {
	AUTH_HTTP_ERROR: EventNames.AUTH_HTTP_ERROR,
	AUTH_LOGOUT: EventNames.AUTH_LOGOUT,
	AUTH_TOKEN_REFRESHED: EventNames.AUTH_TOKEN_REFRESHED,
	AUTH_DATA_SET: EventNames.AUTH_DATA_SET
};
/** Electron IPC channel names for auth events. */
let ElectronAuthIpcEvent = /* @__PURE__ */ function(ElectronAuthIpcEvent) {
	ElectronAuthIpcEvent["TokensWillRefresh"] = "auth:tokens-will-refresh";
	ElectronAuthIpcEvent["TokensDidRefresh"] = "auth:tokens-did-refresh";
	ElectronAuthIpcEvent["TokensDidExpire"] = "auth:tokens-did-expire";
	ElectronAuthIpcEvent["TokensRefreshFailed"] = "auth:tokens-refresh-failed";
	ElectronAuthIpcEvent["AuthDataSet"] = "auth:data-set";
	ElectronAuthIpcEvent["AuthDataCleared"] = "auth:data-cleared";
	ElectronAuthIpcEvent["TokenRequested"] = "auth:token-requested";
	ElectronAuthIpcEvent["AuthDataUsable"] = "auth:data-usable";
	return ElectronAuthIpcEvent;
}({});
//#endregion
//#region src/electron/auth-sync.ts
/** Bridges Electron IPC auth events to globalEventBus for multi-window sync. */
const defaultLogger$3 = Logger.create({ context: "AuthSync" });
let isSetup = false;
let activeLogger = defaultLogger$3;
function getIpcRenderer() {
	return window.ipcRenderer ?? null;
}
/** Setup IPC listener for auth events. Call once at app startup in each window. */
function setupElectronAuthSync(logger) {
	if (!isElectronPlatform() || isSetup) return;
	activeLogger = logger ?? defaultLogger$3;
	const ipcRenderer = getIpcRenderer();
	if (!ipcRenderer) {
		activeLogger.warn("ipcRenderer not available");
		return;
	}
	ipcRenderer.on("auth:data-cleared", (_e, p) => {
		activeLogger.info("Received auth:data-cleared");
		globalEventBus.emit(AuthEventNames.AUTH_LOGOUT, { data: { reason: p.reason } });
	});
	ipcRenderer.on("auth:tokens-did-expire", (_e, _p) => {
		activeLogger.info("Received auth:tokens-did-expire");
		globalEventBus.emit(AuthEventNames.AUTH_LOGOUT, { data: { reason: "session_expired" } });
	});
	ipcRenderer.on("auth:tokens-did-refresh", (_e, p) => {
		activeLogger.info("Received auth:tokens-did-refresh");
		globalEventBus.emit(AuthEventNames.AUTH_TOKEN_REFRESHED, { data: { accessToken: p.accessToken } });
	});
	ipcRenderer.on("auth:data-set", (_e, _p) => {
		activeLogger.info("Received auth:data-set");
		globalEventBus.emit(AuthEventNames.AUTH_DATA_SET, { data: {} });
	});
	isSetup = true;
	activeLogger.info("Electron auth sync initialized");
}
//#endregion
//#region src/errors/auth-errors.ts
/** Auth error types and classes for debugging logout/session expiry scenarios. */
let AuthFailureReason = /* @__PURE__ */ function(AuthFailureReason) {
	AuthFailureReason["REFRESH_TOKEN_EXPIRED"] = "refresh_token_expired";
	AuthFailureReason["REFRESH_TOKEN_REVOKED"] = "refresh_token_revoked";
	AuthFailureReason["REFRESH_TOKEN_MISSING"] = "refresh_token_missing";
	AuthFailureReason["ACCESS_TOKEN_NULL"] = "access_token_null";
	AuthFailureReason["ACCESS_TOKEN_EXPIRED"] = "access_token_expired";
	AuthFailureReason["NETWORK_FAILURE"] = "network_failure";
	AuthFailureReason["SERVER_ERROR"] = "server_error";
	AuthFailureReason["RATE_LIMITED"] = "rate_limited";
	AuthFailureReason["MANUAL_LOGOUT"] = "manual_logout";
	AuthFailureReason["UNKNOWN"] = "unknown";
	return AuthFailureReason;
}({});
/** Error class for auth session failures with detailed context. */
var AuthSessionError = class AuthSessionError extends Error {
	reason;
	details;
	originalError;
	timestamp;
	platform;
	context;
	constructor(reason, options) {
		const message = `Auth failed: ${reason}${options?.details ? ` - ${options.details}` : ""}`;
		super(message);
		this.name = "AuthSessionError";
		this.reason = reason;
		this.details = options?.details;
		this.originalError = options?.originalError;
		this.timestamp = Date.now();
		this.platform = options?.platform ?? (typeof window !== "undefined" && window.electronAPI ? "electron" : "web");
		this.context = { ...options?.context };
		if (Error.captureStackTrace) Error.captureStackTrace(this, AuthSessionError);
	}
	/** Convert to a plain object for logging/serialization. */
	toJSON() {
		return {
			name: this.name,
			reason: this.reason,
			message: this.message,
			details: this.details,
			timestamp: this.timestamp,
			platform: this.platform,
			context: this.context,
			stack: this.stack
		};
	}
};
/** Type guard to check if an error is an AuthSessionError. */
function isAuthSessionError(error) {
	return error instanceof AuthSessionError;
}
/** Maps Auth0 error responses to AuthFailureReason. */
function categorizeAuth0Error(status, body) {
	if (status === 400) {
		if (body?.error === "invalid_grant") return "refresh_token_revoked";
		return "unknown";
	}
	if (status === 401) return "unknown";
	if (status === 403) {
		if (body?.error === "invalid_grant") return "refresh_token_revoked";
		return "refresh_token_expired";
	}
	if (status === 429) return "rate_limited";
	if (status >= 500) return "server_error";
	return "unknown";
}
/** Returns true if the failure reason requires re-authentication. */
function shouldForceLogout(reason) {
	return [
		"refresh_token_expired",
		"refresh_token_revoked",
		"refresh_token_missing"
	].includes(reason);
}
/** Error class for authentication refresh failures. */
var AuthRefreshError = class extends Error {
	reason;
	statusCode;
	constructor(message, reason, statusCode, options) {
		super(message, options);
		this.reason = reason;
		this.statusCode = statusCode;
		this.name = "AuthRefreshError";
	}
};
//#endregion
//#region src/errors/http-errors.ts
/** HTTP error class for API client. */
var HttpError = class HttpError extends Error {
	status;
	body;
	constructor(response, body, message) {
		const errorMessage = message || body?.detail?.user_error_message || body?.detail || `HTTP ${response.status}: ${response.statusText || "Error"}`;
		super(errorMessage);
		this.name = "HttpError";
		this.status = response.status;
		this.body = body;
		Object.setPrototypeOf(this, HttpError.prototype);
	}
};
//#endregion
//#region src/core/api-client.ts
/** HTTP client with automatic auth refresh, retry logic, and error handling. */
const defaultLogger$2 = Logger.create({ context: "ApiClient" });
const MAX_RETRIES = 3;
/** The `detail` field of an error response's JSON body, or undefined when it has none */
async function readErrorDetail(response) {
	try {
		return (await response.clone().json())?.detail;
	} catch {
		return;
	}
}
const SENSITIVE_HEADERS = /* @__PURE__ */ new Set([
	"authorization",
	"proxy-authorization",
	"cookie",
	"set-cookie"
]);
/** Convert HeadersInit to a plain object for logging, redacting sensitive values. */
function headersToObject(headers) {
	const entries = headers instanceof Headers ? headers.entries() : Array.isArray(headers) ? headers : Object.entries(headers ?? {});
	return Object.fromEntries(Array.from(entries, ([name, value]) => [name, SENSITIVE_HEADERS.has(name.toLowerCase()) ? "<REDACTED>" : value]));
}
function truncateValue(value, maxLength) {
	if (value === void 0 || value === null) return value;
	if (typeof value === "string") {
		if (value.length > maxLength) return value.slice(0, maxLength) + "...[truncated]";
		return value;
	}
	if (Array.isArray(value)) {
		const str = JSON.stringify(value);
		if (str.length > maxLength) return str.slice(0, maxLength) + "...[truncated]";
		return value;
	}
	if (typeof value === "object") {
		const str = JSON.stringify(value);
		if (str.length > maxLength) return str.slice(0, maxLength) + "...[truncated]";
		return value;
	}
	return value;
}
function parseRequestBody(body) {
	if (!body) return void 0;
	if (typeof body === "string") try {
		return JSON.parse(body);
	} catch {
		return body;
	}
	if (body instanceof FormData) return "[FormData]";
	return body;
}
function truncateBody(body, maxLength = 1e3) {
	if (body === void 0 || body === null) return body;
	if (typeof body === "string" || Array.isArray(body)) return truncateValue(body, maxLength);
	if (typeof body === "object") try {
		const result = {};
		for (const [key, value] of Object.entries(body)) result[key] = truncateValue(value, maxLength);
		return result;
	} catch {
		return "[unserializable]";
	}
	return body;
}
var ApiClient = class {
	baseURL;
	tokenManager;
	fetchWithRetry;
	userHeaders = {};
	deviceHeaders;
	deviceHeadersReady;
	logger;
	constructor(baseURL, tokenManager, clientVersion, initialHeaders, deviceHeaders, logger) {
		this.baseURL = baseURL;
		this.tokenManager = tokenManager;
		this.userHeaders = initialHeaders ?? {};
		this.deviceHeaders = { "x-client-version": clientVersion };
		this.logger = logger ?? defaultLogger$2;
		this.deviceHeadersReady = deviceHeaders ? Promise.resolve(deviceHeaders).then((h) => {
			this.deviceHeaders = {
				...this.deviceHeaders,
				...h
			};
		}).catch((e) => this.logger.warn("Failed to resolve device headers", { error: e })) : Promise.resolve();
		this.fetchWithRetry = fetchRetry(fetch, {
			retryDelay: function(attempt) {
				return Math.pow(2, attempt) * 1e3;
			},
			retryOn: function(attempt, _error, response) {
				if (attempt >= MAX_RETRIES) return false;
				return !!response && response.status >= 500 && response.status < 600;
			}
		});
	}
	/** Set user-specific headers to include in all requests. */
	setUserHeaders(headers) {
		this.userHeaders = headers;
	}
	/** Without `accessToken`, the headers have no `Authorization` */
	async buildHeaders(customHeaders, accessToken) {
		await this.deviceHeadersReady;
		if (accessToken !== void 0) return {
			Authorization: `Bearer ${accessToken}`,
			...this.deviceHeaders,
			...this.userHeaders,
			...customHeaders
		};
		return {
			...this.deviceHeaders,
			...this.userHeaders,
			...customHeaders
		};
	}
	buildURL(endpoint, params) {
		const normalizedEndpoint = endpoint.replace(/^\//, "");
		const url = `${this.baseURL}/${normalizedEndpoint}`;
		if (!params) return url;
		const searchParams = new URLSearchParams();
		Object.entries(params).forEach(([key, value]) => {
			if (value !== void 0) searchParams.append(key, String(value));
		});
		const queryString = searchParams.toString();
		return queryString ? `${url}?${queryString}` : url;
	}
	async fetchAndParse(endpoint, init) {
		const url = this.buildURL(endpoint);
		const response = await this.fetch(endpoint, init);
		if (response.status === 204) return;
		if (!response.ok) {
			let errorBody;
			try {
				errorBody = await response.json();
			} catch {
				try {
					errorBody = { detail: await response.text() };
				} catch {
					errorBody = void 0;
				}
			}
			const requestHeaders = await this.buildHeaders(init?.headers, init?.skipAuth ? void 0 : "<REDACTED>");
			const telemetry = {
				url,
				method: init?.method ?? "GET",
				headers: headersToObject(requestHeaders),
				body: truncateBody(parseRequestBody(init?.body)),
				status: response.status,
				statusText: response.statusText,
				responseHeaders: headersToObject(response.headers),
				responseBody: truncateBody(errorBody),
				errorType: "HttpError",
				errorMessage: errorBody?.detail?.user_error_message || errorBody?.detail || `HTTP ${response.status}`
			};
			this.logger.error("HTTP request failed", {
				...telemetry,
				type: "http"
			});
			const userErrorMessage = errorBody?.detail?.user_error_message;
			if (userErrorMessage && init?.emitHttpErrorEvent !== false) globalEventBus.emit(AuthEventNames.AUTH_HTTP_ERROR, { data: {
				message: userErrorMessage,
				statusCode: response.status
			} });
			throw new HttpError(response, errorBody);
		}
		const contentType = response.headers.get("content-type")?.toLowerCase();
		if (!contentType || !contentType.includes("application/json")) return await response.text();
		return response.json();
	}
	/** Handle errors from token refresh attempts. */
	handleRefreshError(error) {
		if (isNetworkError(error)) throw error;
		if (isAuthSessionError(error) && shouldForceLogout(error.reason)) globalEventBus.emit(AuthEventNames.AUTH_LOGOUT, { data: { reason: "session_expired" } });
		throw error;
	}
	/** Core fetch method with retry logic. */
	async fetch(endpoint, init) {
		const normalizedEndpoint = endpoint.replace(/^\//, "");
		const url = `${this.baseURL}/${normalizedEndpoint}`;
		const skipAuth = init?.skipAuth;
		let accessToken;
		let headers;
		try {
			accessToken = skipAuth ? void 0 : await this.tokenManager.getAccessToken();
			headers = await this.buildHeaders(init?.headers, accessToken);
		} catch (error) {
			if (!skipAuth) this.handleRefreshError(error);
			throw error;
		}
		const fetchFn = init?.retryOptions ? fetchRetry(fetch, init.retryOptions) : this.fetchWithRetry;
		try {
			const response = await fetchFn(url, {
				...init,
				headers
			});
			if (response.status === 401 && accessToken !== void 0) {
				const detail = await readErrorDetail(response);
				let newAccessToken;
				try {
					newAccessToken = await this.tokenManager.tokenAfterUnauthorized(accessToken, detail);
				} catch (refreshError) {
					this.handleRefreshError(refreshError);
				}
				return await fetchFn(url, {
					...init,
					headers: await this.buildHeaders(init?.headers, newAccessToken)
				});
			}
			return response;
		} catch (error) {
			if (isNetworkError(error)) {
				const telemetry = {
					url,
					method: init?.method ?? "GET",
					headers: headers ? headersToObject(headers) : {},
					body: truncateBody(parseRequestBody(init?.body)),
					errorType: "NetworkError",
					errorMessage: error instanceof Error ? error.message : "Network error"
				};
				this.logger.error("Network request failed", {
					...telemetry,
					type: "http"
				});
				throw new NetworkError();
			}
			throw error;
		}
	}
	/** GET request with automatic JSON parsing. */
	async get(endpoint, options) {
		const { params, ...restOptions } = options ?? {};
		const url = this.buildURL(endpoint, params);
		return this.fetchAndParse(url.replace(`${this.baseURL}/`, ""), {
			...restOptions,
			method: "GET"
		});
	}
	/** POST request with JSON body and automatic response parsing. */
	async post(endpoint, body, options) {
		return this.fetchAndParse(endpoint, {
			...options,
			method: "POST",
			body: body ? JSON.stringify(body) : void 0,
			headers: {
				...options?.headers,
				"Content-Type": "application/json"
			}
		});
	}
	/** PUT request with JSON body and automatic response parsing. */
	async put(endpoint, body, options) {
		return this.fetchAndParse(endpoint, {
			...options,
			method: "PUT",
			body: body ? JSON.stringify(body) : void 0,
			headers: {
				...options?.headers,
				"Content-Type": "application/json"
			}
		});
	}
	/** POST request for FormData (file uploads). */
	async postFormData(endpoint, body, options) {
		return this.fetchAndParse(endpoint, {
			...options,
			method: "POST",
			body
		});
	}
	/** PUT request for FormData (file uploads). */
	async putFormData(endpoint, body, options) {
		return this.fetchAndParse(endpoint, {
			...options,
			method: "PUT",
			body
		});
	}
	/** PATCH request with JSON body and automatic response parsing. */
	async patch(endpoint, body, options) {
		return this.fetchAndParse(endpoint, {
			...options,
			method: "PATCH",
			body: body ? JSON.stringify(body) : void 0,
			headers: {
				...options?.headers,
				"Content-Type": "application/json"
			}
		});
	}
	/** DELETE request with automatic JSON parsing. */
	async delete(endpoint, options) {
		return this.fetchAndParse(endpoint, {
			...options,
			method: "DELETE"
		});
	}
};
//#endregion
//#region src/core/token-manager.ts
/** Centralized token management with platform abstraction and refresh deduplication. */
const defaultLogger$1 = Logger.create({ context: "TokenManager" });
const EXPIRED_TOKEN_DETAIL = "Token has expired";
var TokenManager = class {
	storage;
	platformAuth;
	auth0Config;
	refreshPromise = null;
	cleanedUp = false;
	tokenExpiryBufferSeconds;
	logger;
	constructor(storage, platformAuth, auth0Config, tokenExpiryBufferSeconds, logger) {
		this.storage = storage;
		this.platformAuth = platformAuth;
		this.auth0Config = auth0Config;
		this.tokenExpiryBufferSeconds = tokenExpiryBufferSeconds ?? 30;
		this.logger = logger ?? defaultLogger$1;
	}
	/** Get the current access token, refreshing only if necessary. */
	async getAccessToken() {
		const cachedToken = this.storage.getAccessToken();
		if (cachedToken && !this.isTokenExpired(cachedToken)) return cachedToken;
		return (await this.refreshTokens()).accessToken;
	}
	/** Quick synchronous check if user has a valid (non-expired) token. */
	hasValidToken() {
		try {
			const cachedToken = this.storage.getAccessToken();
			return !!cachedToken && !this.isTokenExpired(cachedToken);
		} catch {
			return false;
		}
	}
	/** Check if a JWT token is expired (with buffer for network latency). */
	isTokenExpired(token, bufferSeconds) {
		try {
			const decoded = jwtDecode(token);
			const now = Math.floor(Date.now() / 1e3);
			const buffer = bufferSeconds ?? this.tokenExpiryBufferSeconds;
			return decoded.exp - buffer <= now;
		} catch {
			return true;
		}
	}
	/**
	* Get a token to retry a request whose token, `rejectedToken`, got a 401
	* with body `detail`. A 401 that says the token expired replaces that token
	* even when the local clock says it is valid. Any other 401 refreshes
	* without passing the token as rejected, since that would make the Electron
	* main process refresh through Auth0, rotating the refresh token, for 401s a
	* new token may not fix.
	*/
	async tokenAfterUnauthorized(rejectedToken, detail) {
		if (rejectedToken !== void 0 && detail === EXPIRED_TOKEN_DETAIL) return this.replaceRejectedToken(rejectedToken);
		await this.refreshTokens();
		return this.getAccessToken();
	}
	/**
	* A stored token other than `rejectedToken` is returned with no refresh.
	* Otherwise this waits for any refresh in flight and, if that did not
	* replace the token, starts a refresh that passes `rejectedToken` to the
	* platform.
	*/
	async replaceRejectedToken(rejectedToken) {
		const storedToken = this.storage.getAccessToken();
		if (storedToken && storedToken !== rejectedToken) return storedToken;
		while (this.refreshPromise) {
			const { accessToken } = await this.refreshPromise;
			if (accessToken !== rejectedToken) return accessToken;
		}
		return (await this.startOrJoinRefresh(rejectedToken)).accessToken;
	}
	/** Refresh tokens, deduplicating concurrent requests. */
	async refreshTokens() {
		return this.startOrJoinRefresh();
	}
	/**
	* Refresh tokens. Concurrent calls share one refresh. On Electron,
	* `rejectedToken` tells the main process which token the server rejected.
	*/
	async startOrJoinRefresh(rejectedToken) {
		if (this.refreshPromise) return this.refreshPromise;
		this.refreshPromise = this.performRefresh(rejectedToken);
		try {
			const tokens = await this.refreshPromise;
			globalEventBus.emit(AuthEventNames.AUTH_TOKEN_REFRESHED, { data: { accessToken: tokens.accessToken } });
			return tokens;
		} finally {
			this.refreshPromise = null;
		}
	}
	/** Perform the actual refresh based on platform. */
	async performRefresh(rejectedToken) {
		if (this.platformAuth) return this.refreshViaPlatform(rejectedToken);
		return this.refreshViaAuth0();
	}
	/** Refresh tokens via platform adapter (e.g., Electron IPC). */
	async refreshViaPlatform(rejectedToken) {
		if (!this.platformAuth) throw new AuthSessionError("unknown", { details: "Platform auth adapter not available" });
		const response = await this.platformAuth.requestToken("frontend", { rejectedToken });
		if (response.status === "failed") {
			const level = response.reason === "suspended" ? "warn" : "error";
			this.logger[level]("Platform token refresh failed", {
				type: "http",
				platform: "electron",
				reason: response.reason,
				error: response.error
			});
			if (response.reason === "network_failure" || response.reason === "suspended") throw new NetworkError();
			throw new AuthSessionError(this.mapPlatformReason(response.reason), {
				details: response.error,
				platform: "electron"
			});
		}
		if (!response.accessToken) {
			this.logger.error("Platform token refresh returned no access token", {
				type: "http",
				platform: "electron",
				responseStatus: response.status
			});
			throw new AuthSessionError("unknown", {
				details: "No access token in platform response",
				platform: "electron"
			});
		}
		if (!this.cleanedUp) this.storage.setAccessToken(response.accessToken);
		return {
			accessToken: response.accessToken,
			refreshToken: response.refreshToken
		};
	}
	/** Handle Auth0 error response. */
	async handleAuth0ErrorResponse(response) {
		const body = await response.json().catch(() => ({}));
		const reason = categorizeAuth0Error(response.status, body);
		const errorMessage = body.error_description || body.error || `HTTP ${response.status}`;
		this.logger.error("Auth0 token refresh failed", {
			type: "http",
			url: response.url,
			method: "POST",
			status: response.status,
			statusText: response.statusText,
			responseBody: body,
			errorType: "AuthSessionError",
			errorMessage,
			reason,
			platform: "web"
		});
		throw new AuthSessionError(reason, {
			details: errorMessage,
			platform: "web"
		});
	}
	/** Handle fetch errors during Auth0 refresh. */
	handleAuth0FetchError(error, url) {
		if (isNetworkError(error)) {
			this.logger.error("Auth0 token refresh network error", {
				type: "http",
				url,
				method: "POST",
				errorType: "NetworkError",
				errorMessage: error instanceof Error ? error.message : "Network error",
				platform: "web"
			});
			throw new NetworkError();
		}
		const errorMessage = error instanceof Error ? error.message : String(error);
		this.logger.error("Auth0 token refresh unexpected error", {
			type: "http",
			url,
			method: "POST",
			errorType: error instanceof Error ? error.constructor.name : "Unknown",
			errorMessage,
			platform: "web"
		});
		throw new AuthSessionError("unknown", {
			details: errorMessage,
			originalError: error,
			platform: "web"
		});
	}
	/** Refresh tokens via direct Auth0 API call (web platform). */
	async refreshViaAuth0() {
		const refreshToken = this.storage.getRefreshToken();
		if (!refreshToken) {
			this.logger.error("Token refresh failed: no refresh token", {
				type: "http",
				platform: "web",
				reason: "refresh_token_missing"
			});
			throw new AuthSessionError("refresh_token_missing", { platform: "web" });
		}
		const url = `https://${this.auth0Config.domain}/oauth/token`;
		const requestBody = {
			grant_type: "refresh_token",
			client_id: this.auth0Config.clientId,
			refresh_token: refreshToken
		};
		try {
			const response = await fetch(url, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(requestBody)
			});
			if (response.ok) {
				const data = await response.json();
				if (!data.access_token) {
					this.logger.error("Auth0 token refresh returned no access token", {
						type: "http",
						url,
						method: "POST",
						status: response.status,
						responseBody: data,
						platform: "web"
					});
					throw new AuthSessionError("unknown", {
						details: "No access token in response",
						platform: "web"
					});
				}
				if (!this.cleanedUp) this.storeTokens(data.access_token, data.refresh_token);
				return {
					accessToken: data.access_token,
					refreshToken: data.refresh_token
				};
			}
			return await this.handleAuth0ErrorResponse(response);
		} catch (error) {
			if (error instanceof AuthSessionError) throw error;
			this.handleAuth0FetchError(error, url);
		}
	}
	/** Map platform's failure reason to AuthFailureReason. */
	mapPlatformReason(reason) {
		switch (reason) {
			case "refresh_expired": return "refresh_token_expired";
			case "refresh_revoked": return "refresh_token_revoked";
			case "network_failure": return "network_failure";
			case "no_refresh_token": return "refresh_token_missing";
			default: return "unknown";
		}
	}
	storeTokens(accessToken, refreshToken) {
		this.storage.setAccessToken(accessToken);
		if (refreshToken) this.storage.setRefreshToken(refreshToken);
	}
	/** Reset internal state and revoke tokens. */
	async cleanup(reason) {
		this.cleanedUp = true;
		this.refreshPromise = null;
		const refreshToken = this.storage.getRefreshToken();
		if (this.platformAuth) {
			await this.platformAuth.clearAuthData(reason);
			this.storage.clearTokens();
			return;
		}
		if (refreshToken) try {
			const response = await fetch(`https://${this.auth0Config.domain}/oauth/revoke`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					token: refreshToken,
					client_id: this.auth0Config.clientId
				})
			});
			if (!response.ok) this.logger.warn("Failed to revoke refresh token", {
				status: response.status,
				statusText: response.statusText
			});
		} catch (error) {
			this.logger.warn("Error revoking refresh token", { error });
		}
		this.storage.clearTokens();
	}
};
//#endregion
//#region src/core/auth-module.ts
/** Auth infrastructure aggregating TokenManager and ApiClient per session. */
const defaultLogger = Logger.create({ context: "AuthModule" });
function resolveElectronDeviceHeaders() {
	if (!isElectronPlatform()) return void 0;
	const ipc = window.ipcRenderer;
	if (!ipc?.invoke) return void 0;
	return Promise.all([ipc.invoke("get-device-id"), ipc.invoke("get-session-id")]).then(([deviceId, sessionId]) => {
		const headers = {};
		if (deviceId) headers["x-device-id"] = String(deviceId);
		if (sessionId) headers["x-session-id"] = String(sessionId);
		headers["x-request-source"] = `renderer:${window.systemInfo?.currentWindow?.label ?? "unknown"}`;
		return headers;
	});
}
/** Auth infrastructure for one session, independent of the shared app instance. */
var AuthModule = class {
	tokenManager;
	apiClient;
	logger;
	constructor(options) {
		const { baseUrl, auth0Domain, auth0ClientId } = options;
		const storage = options.storage ?? new LocalStorageAdapter();
		const platformAuth = options.platformAuth ?? createPlatformAdapter();
		this.logger = options.logger ?? defaultLogger;
		this.logger.debug("Initializing AuthModule", {
			baseUrl,
			auth0Domain,
			hasPlatformAuth: !!platformAuth
		});
		this.tokenManager = new TokenManager(storage, platformAuth, {
			domain: auth0Domain,
			clientId: auth0ClientId
		}, options.tokenExpiryBufferSeconds, options.logger);
		this.apiClient = new ApiClient(baseUrl, this.tokenManager, options.clientVersion ?? "unknown", options.initialHeaders, resolveElectronDeviceHeaders(), options.logger);
		this.logger.debug("AuthModule initialized successfully");
	}
	/** Check if user is authenticated (has valid token). */
	isAuthenticated() {
		return this.tokenManager.hasValidToken();
	}
	/** Get access token, refreshing if necessary. */
	async getAccessToken() {
		return this.tokenManager.getAccessToken();
	}
	/** Logout user - revoke backend token, clear tokens, and emit logout event. */
	async logout(reason = "manual") {
		this.logger.debug("Logging out user", { reason });
		try {
			await this.apiClient.post("/logout");
		} catch {
			this.logger.warn("Backend logout call failed (best-effort)");
		}
		await this.tokenManager.cleanup(reason);
		globalEventBus.emit(AuthEventNames.AUTH_LOGOUT, { data: { reason } });
		this.logger.debug("Logout complete");
	}
};
let appAuthModule;
/** Initialize the shared app instance and Electron event sync. */
function createAuthModule(options) {
	if (!appAuthModule) appAuthModule = new AuthModule(options);
	else (options.logger ?? defaultLogger).warn("AuthModule already initialized. Returning existing instance.");
	setupElectronAuthSync(options.logger);
	return appAuthModule;
}
/** Get AuthModule singleton instance. Throws if not initialized. */
function getAuthModule() {
	if (!appAuthModule) throw new Error("Auth module not initialized. Call createAuthModule() first.");
	return appAuthModule;
}
//#endregion
//#region src/core/create-api-client.ts
var ApiError = class extends Error {
	status;
	body;
	constructor(message, status, body) {
		super(message);
		this.status = status;
		this.body = body;
		this.name = "ApiError";
	}
};
function normalizeBaseUrl(baseUrl) {
	const normalizedBaseUrl = baseUrl.trim().replace(/\/+$/, "");
	if (!normalizedBaseUrl) throw new Error("Backend API base URL is not configured");
	return normalizedBaseUrl;
}
function buildUrl(baseUrl, path, params) {
	const normalizedPath = path.replace(/^\/+/, "");
	const url = new URL(`${normalizeBaseUrl(baseUrl)}/${normalizedPath}`);
	for (const [key, value] of Object.entries(params ?? {})) if (value !== void 0 && value !== null) url.searchParams.set(key, String(value));
	return url.toString();
}
async function readBody(response) {
	if ((response.headers.get("content-type")?.toLowerCase() ?? "").includes("application/json")) return response.json().catch(() => void 0);
	return response.text().catch(() => void 0);
}
function createRequestSignal(options) {
	if (options?.timeoutMs === void 0) return {
		signal: options?.signal,
		cleanup: () => {}
	};
	const controller = new AbortController();
	const abort = () => controller.abort();
	const timeoutId = setTimeout(abort, options.timeoutMs);
	if (options.signal?.aborted) abort();
	else options.signal?.addEventListener("abort", abort, { once: true });
	return {
		signal: controller.signal,
		cleanup: () => {
			clearTimeout(timeoutId);
			options.signal?.removeEventListener("abort", abort);
		}
	};
}
/** Create an HTTP client that injects auth + common headers for every request. */
function createApiClient(config) {
	async function request(method, path, body, options) {
		const token = await config.getAccessToken();
		const headers = {
			Accept: "application/json",
			...body === void 0 ? {} : { "Content-Type": "application/json" },
			...config.getHeaders?.() ?? {},
			...options?.headers,
			...token ? { Authorization: `Bearer ${token}` } : {}
		};
		const requestSignal = createRequestSignal(options);
		try {
			const response = await fetch(buildUrl(config.getBaseUrl(), path, options?.params), {
				method,
				headers,
				body: body === void 0 ? void 0 : JSON.stringify(body),
				signal: requestSignal.signal
			});
			if (!response.ok) {
				const responseBody = await readBody(response);
				throw new ApiError(`${method} ${path} failed (${response.status})`, response.status, responseBody);
			}
			if (response.status === 204) return void 0;
			return await readBody(response);
		} finally {
			requestSignal.cleanup();
		}
	}
	return {
		get: (path, options) => request("GET", path, void 0, options),
		post: (path, body, options) => request("POST", path, body, options),
		put: (path, body, options) => request("PUT", path, body, options),
		delete: (path, options) => request("DELETE", path, void 0, options)
	};
}
//#endregion
//#region src/index.ts
/** API client delegate. Requires createAuthModule() before use. */
const apiClient = {
	setUserHeaders(headers) {
		getAuthModule().apiClient.setUserHeaders(headers);
	},
	fetch(endpoint, init) {
		return getAuthModule().apiClient.fetch(endpoint, init);
	},
	get(endpoint, options) {
		return getAuthModule().apiClient.get(endpoint, options);
	},
	post(endpoint, body, options) {
		return getAuthModule().apiClient.post(endpoint, body, options);
	},
	put(endpoint, body, options) {
		return getAuthModule().apiClient.put(endpoint, body, options);
	},
	postFormData(endpoint, body, options) {
		return getAuthModule().apiClient.postFormData(endpoint, body, options);
	},
	putFormData(endpoint, body, options) {
		return getAuthModule().apiClient.putFormData(endpoint, body, options);
	},
	patch(endpoint, body, options) {
		return getAuthModule().apiClient.patch(endpoint, body, options);
	},
	delete(endpoint, options) {
		return getAuthModule().apiClient.delete(endpoint, options);
	}
};
/** Set user-specific headers for all API requests. Returns false if auth not initialized. */
function setUserHeaders(headers) {
	try {
		getAuthModule().apiClient.setUserHeaders(headers);
		return true;
	} catch (_error) {
		return false;
	}
}
//#endregion
export { ApiClient, ApiError, AuthEventNames, AuthFailureReason, AuthRefreshError, AuthSessionError, ElectronAuthIpcEvent, HttpError, apiClient, createApiClient, createAuthModule, getAuthModule, isAuthSessionError, isNetworkError, setUserHeaders, shouldForceLogout };

//# sourceMappingURL=index.js.map